import React, { useState } from 'react';
import { Lead } from '../../types/Lead';
import { useLeadGenerationForm } from '../../hooks/useLeadGenerationForm';
import { useAuth } from '../../hooks/useAuth';
import { FormField } from '../ui/FormField';
import { Button } from '../ui/Button';
import { FileUpload } from '../ui/FileUpload';
import { LeadGenerationAttemptsList } from '../leads/LeadGenerationAttemptsList';
import { UI_THEME, FORM_FIELD_CONFIG } from '../../config/constants';


interface GenerateLeadViewProps {
  allLeads: Lead[];
  onLeadToggle: (leadId: string) => void;
  expandedCards: Set<string>;
  onLeadUpdate?: (updatedLead: Lead) => void;
  starredLeadIds: Set<string>;
  onStarChange: (leadId: string, isStarred: boolean) => void;
  onMemberChange?: (leadId: string, isMember: boolean) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  itemsPerPage: number;
}

const GenerateLeadView: React.FC<GenerateLeadViewProps> = ({
  allLeads,
  onLeadToggle,
  expandedCards,
  onLeadUpdate,
  starredLeadIds,
  onStarChange,
  onMemberChange,
  currentPage,
  onPageChange,
  totalPages,
  itemsPerPage
}) => {
  const { user } = useAuth();
  const { state, actions, computed } = useLeadGenerationForm(user?.uid);
  const { companyName, geography, isSubmitting, error } = state;
  const [showUpload, setShowUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent double submissions
    if (isSubmitting) {
      console.log('Form already submitting, ignoring duplicate submit event');
      return;
    }

    await actions.validateAndSubmit();
  };

  const handleFileSelect = (file: File) => {
    setUploadedFile(file);
    console.log('File selected:', file.name, file.type, file.size);
  };



  return (
    <div style={{
      position: 'relative',
      width: '100vw',
      marginLeft: 'calc(-50vw + 50%)',
      padding: `${UI_THEME.spacing.md} 0`,
      backgroundColor: UI_THEME.colors.background.secondary,
      minHeight: 'calc(100vh - 200px)', // Account for TopMenu height
    }}>
      <div className="container" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: UI_THEME.spacing.xl,
        paddingLeft: UI_THEME.spacing.lg,
        paddingRight: UI_THEME.spacing.lg,
      }}>
        {/* Lead Generation Form - Left Column */}
        <div className="lead-generation-form" style={{
          width: '400px',
          minWidth: '350px',
          maxWidth: '450px',
          background: 'linear-gradient(135deg, #fefbff 0%, #fbfcff 50%, #f8faff 100%)',
          borderRadius: '16px',
          padding: `${UI_THEME.spacing.lg} ${UI_THEME.spacing.xl}`,
          boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.15), 0 10px 10px -5px rgba(59, 130, 246, 0.08)',
          border: '1px solid #93c5fd',
          animation: UI_THEME.animations.fadeIn,
          position: 'sticky',
          top: UI_THEME.spacing.md,
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
        <div style={{ position: 'relative', textAlign: 'center', marginBottom: UI_THEME.spacing.sm }}>
          <div style={{
            position: 'absolute',
            top: '-16px',
            right: '-16px',
            display: 'flex',
            gap: UI_THEME.spacing.sm,
          }}>
            <Button
              onClick={() => setShowUpload(!showUpload)}
              variant="secondary"
              size="sm"
              style={{
                backgroundColor: '#1d4ed8',
                borderColor: '#1d4ed8',
                color: 'white',
                fontSize: UI_THEME.fontSize.sm,
                fontWeight: UI_THEME.fontWeight.medium,
                padding: `${UI_THEME.spacing.sm} ${UI_THEME.spacing.md}`,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(29, 78, 216, 0.25)',
                minWidth: 'auto',
                transition: 'all 0.2s ease',
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7,10 12,5 17,10"/>
                <line x1="12" y1="5" x2="12" y2="15"/>
              </svg>
            </Button>
          </div>

          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: UI_THEME.colors.gray[900],
            marginBottom: UI_THEME.spacing.xs,
            letterSpacing: '-0.025em',
          }}>
            Generate New Lead
          </h2>
          <p style={{
            color: UI_THEME.colors.gray[600],
            fontSize: UI_THEME.fontSize.sm,
            lineHeight: '1.5',
            maxWidth: '500px',
            margin: '0 auto',
          }}>
            Enter company details to generate comprehensive lead analysis with AI-powered insights
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ animation: UI_THEME.animations.slideUp }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: UI_THEME.spacing.md, marginBottom: UI_THEME.spacing.md }}>
            <FormField
              id={FORM_FIELD_CONFIG.companyName.id}
              label={FORM_FIELD_CONFIG.companyName.label}
              value={companyName}
              onChange={actions.setCompanyName}
              placeholder={FORM_FIELD_CONFIG.companyName.placeholder}
              required={FORM_FIELD_CONFIG.companyName.required}
              disabled={isSubmitting}
              maxLength={FORM_FIELD_CONFIG.companyName.maxLength}
              error={state.validationErrors.companyName}
              borderColor="#c4b5fd"
              focusBorderColor="#a78bfa"
            />

            <FormField
              id={FORM_FIELD_CONFIG.geography.id}
              label={FORM_FIELD_CONFIG.geography.label}
              value={geography}
              onChange={actions.setGeography}
              placeholder={FORM_FIELD_CONFIG.geography.placeholder}
              required={FORM_FIELD_CONFIG.geography.required}
              disabled={isSubmitting}
              maxLength={FORM_FIELD_CONFIG.geography.maxLength}
              error={state.validationErrors.geography}
              borderColor="#c4b5fd"
              focusBorderColor="#a78bfa"
            />
          </div>

          {error && (
            <div style={{
              marginTop: UI_THEME.spacing.sm,
              marginBottom: UI_THEME.spacing.sm,
              padding: UI_THEME.spacing.md,
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: UI_THEME.spacing.sm,
                color: '#dc2626',
                fontSize: UI_THEME.fontSize.sm,
                fontWeight: UI_THEME.fontWeight.medium,
              }}>
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: UI_THEME.spacing.sm }}>
            <Button
              type="submit"
              disabled={!computed.canSubmit || isSubmitting}
              loading={isSubmitting}
              size="lg"
              onClick={() => {
                if (isSubmitting) {
                  console.log('Button click prevented - already submitting');
                  return;
                }
              }}
              style={{
                width: '100%',
                backgroundColor: isSubmitting ? '#6b7280' : '#1d4ed8',
                borderColor: isSubmitting ? '#6b7280' : '#1d4ed8',
                fontSize: '16px',
                fontWeight: '600',
                height: '48px',
                borderRadius: '12px',
                boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(29, 78, 216, 0.25)',
                transition: 'all 0.2s ease',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.7 : 1,
              }}
            >
              {isSubmitting ? 'Sent' : 'Generate Lead'}
            </Button>
          </div>
        </form>

        {showUpload && (
          <div style={{
            marginTop: UI_THEME.spacing.lg,
            padding: UI_THEME.spacing.lg,
            backgroundColor: '#f8fafc',
            borderRadius: UI_THEME.borderRadius.md,
            border: '1px solid #e2e8f0',
            animation: UI_THEME.animations.slideUp,
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: UI_THEME.spacing.md,
            }}>
              <h3 style={{
                fontSize: UI_THEME.fontSize.lg,
                fontWeight: UI_THEME.fontWeight.medium,
                color: UI_THEME.colors.gray[800],
                margin: 0,
              }}>
                Upload Company List
              </h3>
              <button
                onClick={() => setShowUpload(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: UI_THEME.spacing.xs,
                  borderRadius: '4px',
                  color: UI_THEME.colors.gray[500],
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <FileUpload
              onFileSelect={handleFileSelect}
              acceptedFileTypes={['.xlsx', '.xls']}
              maxFileSize={10}
            />

            {uploadedFile && (
              <div style={{
                marginTop: UI_THEME.spacing.md,
                padding: UI_THEME.spacing.md,
                backgroundColor: UI_THEME.colors.background.primary,
                borderRadius: UI_THEME.borderRadius.sm,
                border: '1px solid #e2e8f0',
              }}>
                <h4 style={{
                  fontSize: UI_THEME.fontSize.base,
                  fontWeight: UI_THEME.fontWeight.medium,
                  color: UI_THEME.colors.gray[800],
                  marginBottom: UI_THEME.spacing.sm,
                }}>
                  Selected File:
                </h4>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: UI_THEME.spacing.sm,
                  color: UI_THEME.colors.gray[600],
                  fontSize: UI_THEME.fontSize.sm,
                  marginBottom: UI_THEME.spacing.md,
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10,9 9,9 8,9"/>
                  </svg>
                  <span>{uploadedFile.name}</span>
                  <span style={{ color: UI_THEME.colors.gray[400] }}>
                    ({(uploadedFile.size / 1024).toFixed(1)} KB)
                  </span>
                </div>


              </div>
            )}
          </div>
        )}
        </div>

        {/* Right Column - Lead Generation Attempts List */}
        <div style={{
          flex: 1,
          minHeight: '400px',
          paddingLeft: UI_THEME.spacing.md,
        }}>
          <LeadGenerationAttemptsList
            allLeads={allLeads}
            onLeadToggle={onLeadToggle}
            expandedCards={expandedCards}
            onLeadUpdate={onLeadUpdate}
            starredLeadIds={starredLeadIds}
            onStarChange={onStarChange}
            onMemberChange={onMemberChange}

            currentPage={currentPage}
            onPageChange={onPageChange}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
          />
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }

        /* Custom scrollbar styling for the lead generation form */
        .lead-generation-form::-webkit-scrollbar {
          width: 8px;
        }

        .lead-generation-form::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        .lead-generation-form::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
          transition: background 0.2s ease;
        }

        .lead-generation-form::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Firefox scrollbar styling */
        .lead-generation-form {
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }

        @media (max-width: 1024px) {
          .container {
            flex-direction: column !important;
            justify-content: center !important;
            gap: ${UI_THEME.spacing.lg} !important;
          }

          .lead-generation-form {
            width: 100% !important;
            min-width: unset !important;
            max-width: 600px !important;
            position: static !important;
            margin: 0 auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
        }

        @media (max-width: 768px) {
          .lead-generation-form {
            padding: ${UI_THEME.spacing.md} ${UI_THEME.spacing.lg} !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GenerateLeadView;